import type { ServiceCalibAwaitOutcome } from 'utils/interfaces'

import { catalogCrossTabLog } from './catalogCrossTabDebug'

/** localStorage so `storage` events propagate across tabs (sessionStorage is per-tab). */
function crossTabGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function crossTabSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // localStorage may be unavailable
  }
}

function crossTabRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // localStorage may be unavailable
  }
}

const busyKey = (robotId: number) => `fms:catalog-mode-busy:${robotId}`

const BUSY_CHANGED_EVENT = 'fms:catalog-mode-busy-changed'

export type CrossTabCatalogModeBusy = {
  actionKey: string
  mode: number
  awaitCalib?: ServiceCalibAwaitOutcome
}

/** Stable snapshot for useSyncExternalStore — must not return new object refs each read. */
const busySnapshotCache = new Map<
  number,
  { raw: string | null; value: CrossTabCatalogModeBusy | null }
>()

function invalidateBusySnapshot(robotId: number): void {
  busySnapshotCache.delete(robotId)
}

function notifyBusyChanged(robotId: number): void {
  window.dispatchEvent(new CustomEvent(BUSY_CHANGED_EVENT, { detail: { robotId } }))
}

export function parseCatalogServiceItemKeyFromActionKey(actionKey: string | undefined): string | null {
  if (!actionKey) {
    return null
  }
  for (const prefix of ['catalog-service-stop:', 'catalog-service:']) {
    if (actionKey.startsWith(prefix)) {
      return actionKey.slice(prefix.length)
    }
  }
  return null
}

export function subscribeCrossTabCatalogModeBusy(
  robotId: number,
  onStoreChange: () => void
): () => void {
  const key = busyKey(robotId)
  const onStorage = (event: StorageEvent) => {
    if (event.key === key) {
      catalogCrossTabLog('storage-event', { key, newValue: event.newValue, source: 'mode-busy' })
      onStoreChange()
    }
  }
  const onCustom = (event: Event) => {
    const detail = (event as CustomEvent<{ robotId?: number }>).detail
    if (detail?.robotId === robotId) {
      onStoreChange()
    }
  }
  window.addEventListener('storage', onStorage)
  window.addEventListener(BUSY_CHANGED_EVENT, onCustom)
  return () => {
    window.removeEventListener('storage', onStorage)
    window.removeEventListener(BUSY_CHANGED_EVENT, onCustom)
  }
}

export function readCrossTabCatalogModeBusy(robotId: number): CrossTabCatalogModeBusy | null {
  const raw = crossTabGetItem(busyKey(robotId))
  const cached = busySnapshotCache.get(robotId)
  if (cached && cached.raw === raw) {
    return cached.value
  }
  if (!raw) {
    busySnapshotCache.set(robotId, { raw: null, value: null })
    return null
  }
  try {
    const parsed = JSON.parse(raw) as CrossTabCatalogModeBusy
    if (!parsed?.actionKey || parsed.mode == null) {
      busySnapshotCache.set(robotId, { raw, value: null })
      return null
    }
    busySnapshotCache.set(robotId, { raw, value: parsed })
    return parsed
  } catch {
    return null
  }
}

export function writeCrossTabCatalogModeBusy(robotId: number, busy: CrossTabCatalogModeBusy | null): void {
  const key = busyKey(robotId)
  const nextRaw = busy ? JSON.stringify(busy) : null
  const prevRaw = crossTabGetItem(key)
  if (nextRaw === prevRaw || (!nextRaw && !prevRaw)) {
    return
  }
  if (nextRaw) {
    crossTabSetItem(key, nextRaw)
  } else {
    crossTabRemoveItem(key)
  }
  invalidateBusySnapshot(robotId)
  catalogCrossTabLog('mode-busy', { robotId, busy, prevRaw: prevRaw ?? null })
  notifyBusyChanged(robotId)
}
